import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories } from '@/mocks/data';
import { allArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  const articles = allArticles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-16 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-folder-unknow-line text-3xl"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-600 mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name} - 黄瓜视频App官方指南`}
        description={category.description}
        keywords={`黄瓜视频app,${category.name},黄瓜视频app使用教程`}
        canonicalUrl={`https://www.example.com/category/${slug}`}
        ogTitle={`${category.name} - 黄瓜视频App官方指南`}
        ogDescription={category.description}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
                { '@type': 'ListItem', position: 2, name: category.name },
              ],
            },
            {
              '@type': 'CollectionPage',
              name: `${category.name} - 黄瓜视频App官方指南`,
              description: category.description,
              url: `https://www.example.com/category/${slug}`,
              isPartOf: { '@id': 'https://www.example.com/#website' },
            },
          ],
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-16 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <nav className="flex items-center mb-8" aria-label="面包屑导航">
            <Link to="/" className="breadcrumb-link">首页</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{category.name}</span>
          </nav>

          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
              <i className={`${category.icon} text-xl md:text-2xl`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                {category.description} · 共 {category.articleCount} 篇文章
              </p>
            </div>
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-300/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="h-[200px] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs text-foreground-500">{article.date}</span>
                      <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-article-line text-3xl"></i>
              </div>
              <p className="text-foreground-600">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}